import {
  sendReport,
  extractReportIdFromResponse,
} from "./sendReport";

/**
 * Player stat/report registration, ported from the old player flow: on first visit it
 * inserts the player's `stat` row and creates the external report (../add_report.php).
 */

export const STAGE_ROW_KEY = "truth_dare_stage_row_id";
const REPORT_ID_CACHE_KEY = "truth_dare_report_id";
const STAGE_SESSION_USER_KEY = "truth_dare_stage_session_user";

function getStageSessionUserId(storedUser) {
  if (!storedUser) return "";
  return String(
    storedUser.userId || storedUser.userid || storedUser.id || ""
  ).trim();
}

/** Drop cached progress when switching players in the same browser tab. */
export function clearGameProgressSession() {
  sessionStorage.removeItem(STAGE_ROW_KEY);
  sessionStorage.removeItem(REPORT_ID_CACHE_KEY);
  sessionStorage.removeItem(STAGE_SESSION_USER_KEY);
}

/**
 * If sessionStorage still holds another user's keys, clear them before continuing.
 */
export function ensureStageSessionForUser(storedUser) {
  const uid = getStageSessionUserId(storedUser);
  if (!uid) return false;

  const boundUser = sessionStorage.getItem(STAGE_SESSION_USER_KEY);
  if (boundUser && boundUser !== uid) {
    clearGameProgressSession();
  }
  sessionStorage.setItem(STAGE_SESSION_USER_KEY, uid);
  return true;
}

function getReportRole(user, isDemoBypass) {
  if (isDemoBypass) return "demobypass";
  const r = String(
    user?.role || user?.user_type || user?.userType || ""
  ).trim();
  if (r.toUpperCase() === "GUEST_USER") return "GUEST_USER";
  return r || "ORG_USER";
}

function nameFromEmail(email) {
  const local = String(email || "")
    .trim()
    .split("@")[0]
    .trim();
  return local && local.toLowerCase() !== "null" ? local : "";
}

export function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  const fromParts = parts.map((p) => String(p).trim()).join(" ").trim();
  if (fromParts) return fromParts;
  // No name on the record — use the part before @ in the email (prachi@… → prachi).
  return nameFromEmail(user.email);
}

function cacheReportId(reportId) {
  if (reportId == null || String(reportId).trim() === "") return;
  sessionStorage.setItem(REPORT_ID_CACHE_KEY, String(reportId).trim());
}

async function linkReportToStage({ backendBase, adminToken, storedUser, stageId, reportId }) {
  const uid = storedUser.userId || storedUser.userid || storedUser.id;
  const linkRes = await fetch(`${backendBase}/welcomeStageLinkReport`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${adminToken}`,
    },
    body: JSON.stringify({
      userId: uid,
      sessionId: storedUser.sessionId,
      organizationId: storedUser.organizationId,
      stageId,
      reportId: String(reportId),
    }),
  });
  const linkData = await linkRes.json().catch(() => ({}));
  if (!linkRes.ok || !linkData.success) {
    console.error("welcomeStageLinkReport:", linkData.message || linkRes.status);
    return;
  }
  cacheReportId(reportId);
  const resolvedStageId =
    linkData.stageId != null ? String(linkData.stageId) : String(stageId);
  if (resolvedStageId) sessionStorage.setItem(STAGE_ROW_KEY, resolvedStageId);
}

async function createExternalReport({ storedUser, isDemoBypass }) {
  const seedRes = await sendReport({
    sessionId: storedUser.sessionId,
    organizationId: storedUser.organizationId,
    role: getReportRole(storedUser, isDemoBypass),
    token: storedUser.token || "",
    points: 0,
    time: "00:00",
    gameId: storedUser.gameId,
    name: normalizePlayerName(storedUser),
    userId: storedUser.userId || storedUser.userid || storedUser.id,
  });

  const newReportId = extractReportIdFromResponse(seedRes);
  if (!newReportId) {
    console.error("sendReport: no report id in response", seedRes);
    return null;
  }
  cacheReportId(newReportId);
  return newReportId;
}

/**
 * Ensure the player has a stat row (welcomeStageStart) and an external report linked to it.
 * Does nothing if the player already has a linked report.
 */
export async function bootstrapStageAndReport({
  backendBase,
  adminToken,
  storedUser,
  isDemoBypass,
}) {
  if (!backendBase || !adminToken || isDemoBypass) return;

  ensureStageSessionForUser(storedUser);

  const uid = storedUser.userId || storedUser.userid || storedUser.id;
  const sessionId = storedUser.sessionId;
  const organizationId = storedUser.organizationId;
  if (!uid || !sessionId || !organizationId) return;

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${adminToken}`,
  };

  try {
    const reportRes = await fetch(`${backendBase}/fetchReport`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ userId: uid, sessionId, organizationId }),
    });
    const reportPayload = await reportRes.json().catch(() => ({}));

    if (reportRes.ok && reportPayload.success && reportPayload.report) {
      const row = reportPayload.report;
      if (row.reportId != null && String(row.reportId).trim() !== "") {
        if (row.id != null) sessionStorage.setItem(STAGE_ROW_KEY, String(row.id));
        cacheReportId(row.reportId);
        return;
      }
    } else {
      sessionStorage.removeItem(STAGE_ROW_KEY);
    }

    const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        userId: uid,
        sessionId,
        organizationId,
        email: storedUser.email || "",
        employeeId: storedUser.employeeId || storedUser.emp_id || "",
        emp_id: storedUser.employeeId || storedUser.emp_id || "",
        Division: storedUser.Division || storedUser.division || "",
        language: storedUser.language || storedUser.lang || "english",
        name: normalizePlayerName(storedUser),
        user_type:
          storedUser.user_type ||
          storedUser.userType ||
          storedUser.role ||
          "",
      }),
    });
    const startData = await startRes.json().catch(() => ({}));

    if (!startRes.ok || !startData.success || startData.id == null) {
      console.error("welcomeStageStart failed:", startData.message || startRes.status);
      return;
    }

    const stageId = String(startData.id);
    sessionStorage.setItem(STAGE_ROW_KEY, stageId);

    const newReportId = await createExternalReport({ storedUser, isDemoBypass });
    if (!newReportId) return;

    await linkReportToStage({ backendBase, adminToken, storedUser, stageId, reportId: newReportId });
  } catch (err) {
    console.error("bootstrapStageAndReport:", err);
  }
}
